import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FacilityListItem } from './FacilityListItem'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { isUserAdminInSociety } from '../../../helpers/society'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { facilityQueries } from '../../../api/facilities'
import { i18nSocietyType } from '../../../i18n'
import { sortStringsAlphabetically } from '../../../helpers/sorting'

export const FacilitiesList = (): React.ReactNode => {
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const userId = useAuthenticatedUserId() as string

  const { data: unsortedFacilities, isLoading: isFetchingFacilities } =
    useQuery(facilityQueries.getFacilitiesPerSociety(society?._id))
  const facilities = useMemo(
    () =>
      unsortedFacilities &&
      [...unsortedFacilities].sort((a, b) =>
        sortStringsAlphabetically(a.name, b.name)
      ),
    [unsortedFacilities]
  )
  const userIsAdmin = isUserAdminInSociety(society, userId as string)
  const loading = isFetchingFacilities

  if (loading) {
    return <SpinnerWrapper />
  }

  if (!loading && facilities && facilities.length === 0) {
    return (
      <ErrorView
        title={translate('facilitiesView.emptyState.noFacilitiesTitle')}
        subtitle={translate('facilitiesView.emptyState.noFacilitiesSubtitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        illustration={IllustrationChoices.CHECKLIST}
      />
    )
  }

  return (
    <div className="w-full space-y-4">
      {facilities?.map((facility) => (
        <FacilityListItem
          key={facility._id}
          facility={facility}
          userIsAdmin={userIsAdmin}
        />
      ))}
    </div>
  )
}
